import { useTranslation } from "react-i18next";
import Navbar from "../Menu/Navbar";
import Footer from "./Sections/Footer";
import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Register from "./Register";
import Preloader from "../../Components/Preloader";
import { Form, Input } from "antd";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../apis/store";
import {
  requestCartSync,
} from "../../apis/actions/cart.actions";
import { toGetProfil, toLogin } from "../../apis/actions/auth.actions";
import { showNotitfication } from "../../utils/Utils";
import { normalizeLoginIdentifier } from "../../utils/normalizeLoginIdentifier";
import { useCookies } from "react-cookie";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ForgotPassword from "./ForgetPassword";
import { publicAsset } from "../../utils/publicAsset";
import {
  AuthIconArrowForward,
  AuthIconLock,
  AuthIconUser,
} from "../../Components/icons/AuthFormIcons";
import { resolveAuthReturnPath } from "../../utils/authReturnPath";

const Login = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");
  const reduceMotion = useReducedMotion();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [, setCookie] = useCookies(["apiKey"]);
  const [loading, setLoading] = useState(false);
  const [toRegister, setToRegister] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [form] = Form.useForm();

  const onFinish = async () => {
    setLoading(true);
    try {
      await form.validateFields();
      let username = normalizeLoginIdentifier(form.getFieldValue("username"));
      const password = form.getFieldValue("password");
      const success = (await dispatch(
        toLogin({
          login: username,
          password,
        })
      )) as any;

      const login = success?.payload?.data?.result;

      if (login?.code === 200) {
        setCookie("apiKey", Object.values(login)[0]);

        const profilData = await dispatch(toGetProfil(i18n.language));
        const profilRecord =
          (profilData as any)?.payload?.data?.result ??
          (profilData as any)?.payload?.data?.data ??
          (profilData as any)?.payload?.data;
        const displayName = [profilRecord?.title, profilRecord?.name]
          .filter((part) => typeof part === "string" && part.trim())
          .join(" ")
          .trim();

        // The server draft replaces browser contents; login never uploads stale storage.
        await dispatch(requestCartSync());

        showNotitfication(
          displayName
            ? `${t("profile.welcome").trim()} ${displayName}`
            : t("profile.welcome").trim(),
          "success"
        );

        setTimeout(() => {
          navigate(resolveAuthReturnPath(location.state), { replace: true });
        }, 2800);
      } else {
        const isAccessDenied = login?.message === "Access Denied";
        const customMessage = isAccessDenied
          ? isRtl
            ? "معلومات الدخول غير صحيحة. يرجى التأكد من إدخال المعلومات الصحيحة"
            : "Incorrect login information. Please make sure to enter the correct login credentials"
          : login?.message;

        showNotitfication(customMessage, "error");
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar type="internal" />
      <Preloader />
      <main className="main ac-login-page">
        <div className="container-fluid ac-login-page__shell pt-40 pb-170">
          {forgotPassword ? (
            <ForgotPassword
              setTorResetPassword={() => setForgotPassword(false)}
            />
          ) : (
            <div className="ac-login-page__grid ac-login-page__split">
              <div className="ac-login-page__hero-slot d-none d-xl-flex flex-column">
                <aside className="ac-login-page__hero flex-grow-1 w-100">
                  <div className="ac-login-page__hero-media" aria-hidden />
                  <div className="ac-login-page__hero-shade" aria-hidden />
                  <div className="ac-login-page__hero-inner">
                    <div className="ac-login-page__hero-top">
                      <Link
                        to="/"
                        className="ac-login-page__logo-link"
                        aria-label={t("menu.home")}
                      >
                        <img
                          src={publicAsset("assets/imgs/template/logo-w.svg")}
                          alt=""
                          className="ac-login-page__logo"
                        />
                      </Link>
                      <span className="ac-login-page__edition ac--font-expo">
                        {t("login.memberAccess")}
                      </span>
                    </div>

                    <div className="ac-login-page__hero-copy">
                      <p className="ac-login-page__hero-kicker ac--font-expo">
                        {toRegister ? t("login.register") : t("login.signin")}
                      </p>
                      <h2 className="ac-login-page__hero-title">
                        {toRegister
                          ? t("register.createAccount")
                          : t("login.heroTitle")}
                      </h2>
                      <p className="ac-login-page__hero-lead ac--font-expo">
                        {toRegister
                          ? t("register.allFieldsRequired")
                          : t("login.lead")}
                      </p>
                    </div>

                    <div className="ac-login-page__journey ac--font-expo">
                      {["book", "garage", "rewards"].map((item, index) => (
                        <div className="ac-login-page__journey-item" key={item}>
                          <span>{String(index + 1).padStart(2, "0")}</span>
                          <p>{t(`login.benefits.${item}`)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </aside>
              </div>
              <div className="ac-login-page__form-slot ac-login-page__form-stack">
                <div className="d-xl-none ac-login-page__hero-mobile ac-login-page__hero-mobile--elevated ac--font-expo">
                  <Link
                    to="/"
                    className="ac-login-page__logo-link-mobile"
                    aria-label={t("menu.home")}
                  >
                    <img
                      src={publicAsset("assets/imgs/template/logo-d.svg")}
                      alt=""
                    />
                  </Link>
                  <p className="ac-login-page__hero-mobile-kicker">
                    {t("login.memberAccess")}
                  </p>
                </div>

                <div
                  className="ac-auth-toggle ac--font-expo"
                  role="tablist"
                  aria-label={`${t("login.signin")} · ${t("register.register")}`}
                >
                  <div className="ac-auth-toggle__track">
                    <motion.div
                      layout
                      className="ac-auth-toggle__indicator"
                      transition={{
                        type: "spring",
                        stiffness: 460,
                        damping: 38,
                      }}
                      style={{
                        position: "absolute",
                        top: 4,
                        bottom: 4,
                        width: "calc(50% - 4px)",
                        insetInlineStart: toRegister
                          ? "calc(50% + 2px)"
                          : 4,
                        borderRadius: 999,
                      }}
                    />
                    <motion.button
                      type="button"
                      role="tab"
                      id="auth-tab-signin"
                      aria-selected={!toRegister}
                      aria-controls="auth-panel-login"
                      className={`ac-auth-toggle__tab${!toRegister ? " is-active" : ""}`}
                      onClick={() => setToRegister(false)}
                      whileTap={{ scale: reduceMotion ? 1 : 0.98 }}
                    >
                      {t("login.signin")}
                    </motion.button>
                    <motion.button
                      type="button"
                      role="tab"
                      id="auth-tab-register"
                      aria-selected={toRegister}
                      aria-controls="auth-panel-register"
                      className={`ac-auth-toggle__tab${toRegister ? " is-active" : ""}`}
                      onClick={() => setToRegister(true)}
                      whileTap={{ scale: reduceMotion ? 1 : 0.98 }}
                    >
                      {t("register.register")}
                    </motion.button>
                  </div>
                </div>

                <div className="ac-login-page__panel">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={toRegister ? "register" : "login"}
                      role="tabpanel"
                      id={
                        toRegister ? "auth-panel-register" : "auth-panel-login"
                      }
                      aria-labelledby={
                        toRegister ? "auth-tab-register" : "auth-tab-signin"
                      }
                      initial={
                        reduceMotion ? false : { opacity: 0, y: 18 }
                      }
                      animate={{ opacity: 1, y: 0 }}
                      exit={
                        reduceMotion ? { opacity: 0 } : { opacity: 0, y: -14 }
                      }
                      transition={
                        reduceMotion
                          ? { duration: 0.12 }
                          : { duration: 0.34, ease: [0.22, 1, 0.36, 1] }
                      }
                      className="ac-login-page__panel-motion"
                    >
                      {toRegister ? (
                        <Register
                          embedded
                          setToRegister={() => setToRegister(false)}
                        />
                      ) : (
                        <div className="ac-login-card">
                          <div className="login-content">
                            <div className="text-center ac-login-card__head">
                              <span className="ac-login-card__eyebrow ac--font-expo">
                                {t("login.signin")}
                              </span>
                              <h4 className="neutral-1000 ac--font-expo ac-login-card__title">
                                {t("login.welcome")}
                              </h4>
                              <p className="ac-login-card__subtitle ac--font-expo neutral-500 d-xl-none">
                                {t("login.lead")}
                              </p>
                            </div>
                            <div className="mt-30">
                              <Form
                                form={form}
                                onFinish={onFinish}
                                onFinishFailed={() => setLoading(false)}
                                className="ac-login-form"
                              >
                        <Form.Item
                          label=""
                          name="username"
                          rules={[
                            {
                              required: true,
                              message: t("profile.messages.7"), // Keep existing required message
                            },
                            {
                              validator: (_, value) => {
                                if (!value) return Promise.resolve();

                                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                const phoneRegex = /^[0-9]{6,15}$/;

                                if (
                                  emailRegex.test(value) ||
                                  phoneRegex.test(value)
                                ) {
                                  return Promise.resolve();
                                }

                                return Promise.reject(
                                  isRtl
                                    ? "يرجى إدخال بريد إلكتروني أو رقم هاتف صحيح"
                                    : "Please enter a valid email address or phone number"
                                );
                              },
                            },
                          ]}
                        >
                          <div className="form-group ac--font-expo">
                            <Input
                              className="ac--input-login"
                              size="large"
                              type="text"
                              placeholder={t("login.emailOrPhone")}
                              prefix={<AuthIconUser />}
                            />
                          </div>
                        </Form.Item>
                        <Form.Item
                          label=""
                          name="password"
                          rules={[
                            {
                              required: true,
                              message: t("profile.messages.4"),
                            },
                          ]}
                        >
                          <div className="form-group ac--password-container ac--font-expo">
                            <Input.Password
                              className="ac--input-login"
                              size="large"
                              type="password"
                              placeholder={t("login.password")}
                              prefix={<AuthIconLock />}
                            />
                          </div>
                        </Form.Item>
                        <div className="form-group">
                          <div className="box-remember-forgot ac-login-page__remember-row">
                            <div className="remeber-me">
                              <label className="text-xs-medium neutral-500 ac--font-expo d-flex align-items-center">
                                <input
                                  className={`cb-remember ${
                                    isRtl ? "ml-5" : "mr-5"
                                  }`}
                                  type="checkbox"
                                />
                                {t("login.remember")}
                              </label>
                            </div>
                            <div className="forgotpass">
                              <button
                                type="button"
                                className="text-xs-medium ac--font-expo ac-login-page__forgot-btn"
                                onClick={() => setForgotPassword(true)}
                              >
                                {t("login.forgot")}
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="form-group mb-30">
                          <button
                            type="submit"
                            className={`btn btn-primary w-100 ac--font-expo ac-login-page__submit${
                              loading ? " is-loading" : ""
                            }`}
                            disabled={loading}
                            aria-busy={loading}
                          >
                            <span className="ac-login-page__submit-glint" aria-hidden />
                            <span className="ac-login-page__submit-label">
                              {loading ? t("login.signingIn") : t("login.enter")}
                            </span>
                            <span
                              className={`ac-auth-arrow-slot ${
                                isRtl ? "ac-auth-arrow--rtl" : ""
                              }`}
                              aria-hidden
                            >
                              {loading ? (
                                <span className="ac-login-page__submit-spinner" />
                              ) : (
                                <AuthIconArrowForward size={16} />
                              )}
                            </span>
                          </button>
                          <p className="ac-login-page__privacy-note ac--font-expo">
                            <span aria-hidden>✓</span>
                            {t("login.secureNote")}
                          </p>
                        </div>
                      </Form>
                            </div>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer type="external" />
    </>
  );
};
export default Login;
